import {useMemo} from 'react'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {createPersister} from '@github-ui/react-query-persister'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {Type} from '@github-ui/react-query-persister/typebox'
import {useAnalytics} from '@github-ui/use-analytics'
import {fetchCAPI} from '../utils/fetch-capi'
import {environmentsApiPath} from '../utils/api-url-helpers'
import type {Environment, EnvironmentKind, EnvironmentStatus} from '../types/environment'

export const environmentsQueryKey = ['agent-environments'] as const

/** Persisted environments older than this are discarded — environment status
 * (online/offline/degraded/draining) is time-sensitive, so we only trust a
 * recently-cached list for an instant first paint before refetching. */
export const ENVIRONMENTS_MAX_AGE_MS = 60_000

export const ENVIRONMENTS_POLL_INTERVAL_MS = 30_000

export const EnvironmentSchema = Type.Object({
  id: Type.String(),
  name: Type.String(),
  kind: Type.Union([
    Type.Literal('managed-actions'),
    Type.Literal('managed-sandbox'),
    Type.Literal('managed-cca'),
    Type.Literal('user-local'),
    Type.Literal('user-codespace'),
  ]),
  status: Type.Union([
    Type.Literal('online'),
    Type.Literal('offline'),
    Type.Literal('degraded'),
    Type.Literal('waking'),
    Type.Literal('draining'),
  ]),
  capabilities: Type.Optional(
    Type.Object({
      ahp_version: Type.Optional(Type.String()),
      features: Type.Optional(Type.Array(Type.String())),
      max_sessions: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
      current_sessions: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
    }),
  ),
  provisioning_mode: Type.Optional(Type.String()),
  infra_health_ref: Type.Optional(Type.String()),
  user_id: Type.Optional(Type.String()),
  org_id: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  labels: Type.Optional(Type.Record(Type.String(), Type.String())),
  last_heartbeat_at: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  // Note: `webpubsub` (which carries a short-lived `access_token`) is
  // deliberately omitted here. The query data is persisted to IndexedDB, and we
  // must never write credentials to disk. The token is stripped from the
  // response before it reaches the cache (see `queryFn`), so it is neither
  // persisted nor validated.
})

const EnvironmentsValidator = Compile(Type.Array(EnvironmentSchema))

interface UseEnvironmentsOptions {
  /** Optional filter by environment kind. */
  kind?: EnvironmentKind
  /** Optional filter by environment status. */
  status?: EnvironmentStatus
  /** Allows callers to disable the query in addition to the feature flag. */
  enabled?: boolean
}

/**
 * Fetches the list of AHP environments (compute targets) available to the
 * current user via the Mission Control `GET /agents/environments` endpoint.
 *
 * Gated behind the `copilot_mission_control_environments_web_kickoff` feature flag.
 * Errors (including server-side flag-gate rejections during rollout) are
 * swallowed and surfaced as an empty list so callers never have to handle a
 * failed request for what is currently a non-critical, additive feature.
 *
 * The most recent response is persisted in IndexedDB (validated, capped at a
 * one-minute max age) so the list can render instantly on subsequent loads
 * while still refetching fresh, time-sensitive status.
 */
export function useEnvironments({kind, status, enabled = true}: UseEnvironmentsOptions = {}) {
  const flagEnabled = isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
  const queryEnabled = flagEnabled && enabled
  const {sendAnalyticsEvent} = useAnalytics()
  const queryClient = useQueryClient()

  const persister = useMemo(
    () =>
      createPersister({
        validator: EnvironmentsValidator,
        sendAnalyticsEvent,
        prefix: 'copilot-agent-environments',
        maxAgeMs: ENVIRONMENTS_MAX_AGE_MS,
        enabled: queryEnabled,
      }),
    [sendAnalyticsEvent, queryEnabled],
  )

  const queryKey = [...environmentsQueryKey, kind ?? null, status ?? null] as const

  return useQuery<Environment[]>({
    queryKey,
    enabled: queryEnabled,
    refetchInterval: ENVIRONMENTS_POLL_INTERVAL_MS,
    queryFn: async () => {
      try {
        const response = await fetchCAPI({path: environmentsApiPath({kind, status})})
        const data = await response.json()
        // The endpoint returns a bare array; guard against unexpected shapes.
        if (!Array.isArray(data)) return []
        // Strip `webpubsub` (which carries an `access_token`) so the credential
        // never enters the query cache or the IndexedDB persister. It is not
        // needed for listing environments and can be fetched fresh when a
        // daemon connection is actually established.
        return (data as Environment[]).map(({webpubsub: _webpubsub, ...environment}) => environment)
      } catch (error) {
        // A transient poll failure must not clobber a good list — rethrow so
        // TanStack Query retains the previously cached environments. Only the
        // initial load (no cache yet) resolves to the empty-state list.
        const cached = queryClient.getQueryData<Environment[]>(queryKey)
        if (cached && cached.length > 0) throw error
        return []
      }
    },
    persister,
  })
}
